import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@app/redux/store/root";
import { AuthModel, Setting } from "@app/redux/models";
import { roleNameIdMap } from "@app/utils/roleNameIdMap";

/**
 * Custom hook to check if a feature toggle is enabled for given feature_name and env.
 * @param {string} featureName - The name of the feature toggle to check.
 * @param {boolean} defaultIsEnabled - The default value of isEnabled if there is no setting for the feature conditions in the database.
 * @returns {boolean} - Returns `true` if the feature toggle is enabled, else `false`.
 */

const useFeatureToggle = (featureName: string, defaultIsEnabled: boolean = true): boolean => {
    const [isEnabled, setIsEnabled] = useState<boolean>(defaultIsEnabled);
    const AWS_ENV = `${process.env.AWS_ENV}`;
    const OEM = `${process.env.OEM}`;

    const auth: AuthModel = useSelector((state: RootState) => state.authDetails);

    const featureSettings = useSelector((state: RootState) => state.featureSettings.settings);

    const roleId = roleNameIdMap(auth?.userRole);

    useEffect(() => {
        if (featureSettings.length > 0) {
            const isForFeatureName = (item: Setting) => item.feature_name === featureName;
            const isForEnvironment = (item: Setting) => item.toggle_env === AWS_ENV;
            const isForRole = (item: Setting) => item.role_id === null || item.role_id === roleId;
            const isForOEM = (item: Setting) => item.oem_name === null || item.oem_name === OEM;

            const matchedSetting = featureSettings.find(
                (item: Setting) =>
                    isForFeatureName(item) &&
                    isForEnvironment(item) &&
                    isForRole(item) &&
                    isForOEM(item)
            );

            if (matchedSetting) {
                setIsEnabled(matchedSetting.is_enabled);
            }
        }
    }, [featureName, AWS_ENV, featureSettings, roleId, OEM]);

    return isEnabled;
};

export default useFeatureToggle;
